/* eslint-disable max-classes-per-file */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

import type { IntroVideoEntrypointValue } from './sharedConstants';

/**
 * The client half of the intro video upload funnel. The backend reports the rest
 * (review, approval, takedowns) from `sitter_intro_videos/analytics.py`, joined on
 * `sitter_intro_video_id`.
 *
 * The observability tags become the Datadog metric's tags, so they hold only the
 * low-cardinality fields the dashboard splits on.
 */

export type IntroVideoUploadFailureStage = 'pick' | 'upload';
export type IntroVideoUploadFailureReason = 'error' | 'cancelled';

export class IntroVideoUploadEntrypointClicked extends AnalyticsEvent {
  eventName = 'intro-video-upload-entrypoint-clicked';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: { entrypoint: IntroVideoEntrypointValue }) {
    super(eventProperties, { entrypoint: eventProperties.entrypoint });
  }
}

export class IntroVideoUploadStarted extends AnalyticsEvent {
  eventName = 'intro-video-upload-started';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: { entrypoint: IntroVideoEntrypointValue; is_replace: boolean }) {
    super(eventProperties, {
      entrypoint: eventProperties.entrypoint,
      is_replace: eventProperties.is_replace,
    });
  }
}

export class IntroVideoUploadCompleted extends AnalyticsEvent {
  eventName = 'intro-video-upload-completed';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: {
    entrypoint: IntroVideoEntrypointValue;
    upload_duration_ms: number;
    sitter_intro_video_id: string | undefined;
  }) {
    super(eventProperties, { entrypoint: eventProperties.entrypoint });
  }
}

export class IntroVideoUploadFailed extends AnalyticsEvent {
  eventName = 'intro-video-upload-failed';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: {
    entrypoint: IntroVideoEntrypointValue;
    failure_stage: IntroVideoUploadFailureStage;
    reason: IntroVideoUploadFailureReason;
  }) {
    super(eventProperties, {
      entrypoint: eventProperties.entrypoint,
      failure_stage: eventProperties.failure_stage,
      reason: eventProperties.reason,
    });
  }
}
